/* global JQuery */
import { Card2, ContentCol2, ContentColSize2, ContentRow2, LangTextV2 } from 'bambooo';
import * as d3raw from 'd3';
import { Species as SpeciesAPI } from '../../Api/Species';
import { Lang } from '../../Lang';
import { UtilBootstrap } from '../../Utils/UtilBootstrap';
import { BasePage } from '../BasePage';
/*
 * Same d3-as-any pattern the rest of the project uses (see
 * TrackingChart, MetricCharts, Species/Profile). Keeps the chart code
 * readable; the surrounding TS stays fully typed.
 */
// eslint-disable-next-line @typescript-eslint/no-explicit-any
const d3 = d3raw;
/**
 * Cross-Species Analytics
 *
 * Companion page to the per-species Profile. Shows four scatter charts
 * with two regression overlays each: solid colored lines per species,
 * a thicker dashed black line for the pooled regression. The point of
 * the page is to surface Simpson-paradox effects — when aggregating
 * across species reverses the per-species trend, that's visible at a
 * glance because the dashed line points the "wrong way" relative to
 * the colored ones.
 *
 * The OLS math sits server-side (Profile route) so the page is a thin
 * d3 renderer plus a small helper for the regression-line endpoints.
 */
export class CrossSpeciesAnalytics extends BasePage {
    constructor() {
        super(...arguments);
        this._name = CrossSpeciesAnalytics.NAME;
        /**
         * Latest fetched charts + stable color map, kept on the instance so the
         * PDF handler can re-render the same data at print width without a
         * second API round-trip.
         */
        this._lastCharts = [];
        this._lastColors = new Map();
        this._chartHosts = {};
    }
    async loadContent() {
        const lang = Lang.i();
        const title = lang.l('Cross-species analytics');
        const contentWrapper = this._wrapper.getContentWrapper().getContent();
        // Header card — short explainer ------------------------------------------------------------------------------
        const rowHead = new ContentRow2(contentWrapper.getContentFluidElement());
        const headCard = new Card2({});
        headCard.appendTo(new ContentCol2(rowHead, ContentColSize2.col12).getElement());
        headCard.setTitle(new LangTextV2({ text: title }));
        headCard.getMainElement().addClass('print-include');
        const headBody = jQuery('<div class="card-body small text-muted"/>').appendTo(headCard.getBodyElement());
        headBody.text(lang.l('desc.regression.page'));
        // PDF export button — sits in the head card so it's always visible
        // at the top of the page. Hidden during @media print via CSS.
        const exportWrap = jQuery('<div class="cross-species-export text-right mt-2"/>').appendTo(headBody);
        const pdfBtn = jQuery('<button type="button" class="btn btn-primary btn-sm">'
            + `<i class="fa fa-file-pdf"></i> ${this._escape(lang.l('Generate PDF'))}`
            + '</button>').appendTo(exportWrap);
        pdfBtn.on('click', async () => {
            if (this._lastCharts.length === 0) {
                return;
            }
            const originalLabel = pdfBtn.html();
            pdfBtn.prop('disabled', true);
            pdfBtn.html(`<i class="fa fa-spinner fa-spin"></i> ${this._escape(lang.l('Generating…'))}`);
            try {
                await this._generatePdf();
            }
            catch (e) {
                console.error('[Cross-species PDF] generate failed', e);
                window.alert(`${lang.l('Download failed')}: ${e.message}`);
            }
            finally {
                pdfBtn.html(originalLabel);
                pdfBtn.prop('disabled', false);
            }
        });
        // Four chart cards (each row owns one chart) -----------------------------------------------------------------
        const hosts = {};
        const cardSpecs = [
            { id: 'year_spue', titleKey: 'Year × SPUE', descKey: 'desc.reg.year_spue' },
            { id: 'sst_groupsize', titleKey: 'SST × group size', descKey: 'desc.reg.sst_groupsize' },
            { id: 'chl_groupsize', titleKey: 'Chlorophyll-a × group size', descKey: 'desc.reg.chl_groupsize' },
            { id: 'effort_saturation', titleKey: 'Tour effort × sightings', descKey: 'desc.reg.effort' }
        ];
        for (const spec of cardSpecs) {
            const row = new ContentRow2(contentWrapper.getContentFluidElement());
            const card = new Card2({});
            card.appendTo(new ContentCol2(row, ContentColSize2.col12).getElement());
            card.setTitle(new LangTextV2({ text: spec.titleKey }));
            card.getMainElement().addClass('print-include');
            CrossSpeciesAnalytics._attachInfo(card, spec.titleKey, spec.descKey);
            // Print-only description block — hidden on screen, shown when
            // body has `mwpa-print-mode-species`. Mirrors the (i)-popover
            // text so the PDF reader can interpret the chart without
            // having opened the popover beforehand.
            jQuery('<div class="cross-species-print-desc"/>')
                .text(lang.l(spec.descKey))
                .appendTo(card.getBodyElement());
            const host = jQuery('<div class="cross-species-chart"/>').appendTo(card.getBodyElement());
            hosts[spec.id] = host[0];
        }
        this._chartHosts = hosts;
        this._onLoadTable = async () => {
            headCard.showLoading();
            try {
                const charts = await SpeciesAPI.getRegressionMatrix();
                if (!charts || charts.length === 0) {
                    return;
                }
                // Build a stable species→color map across every chart so
                // the same species reads the same on every plot.
                const colorBySpecies = CrossSpeciesAnalytics._buildColorMap(charts);
                this._lastCharts = charts;
                this._lastColors = colorBySpecies;
                this._renderAll();
                Lang.i().lAll();
            }
            finally {
                headCard.hideLoading();
            }
        };
        await this._onLoadTable();
    }
    _renderAll() {
        for (const chart of this._lastCharts) {
            const host = this._chartHosts[chart.id];
            if (host) {
                CrossSpeciesAnalytics._renderScatter(host, chart, this._lastColors);
            }
        }
    }
    /**
     * Drive the browser's print dialog with body-level print-mode classes
     * applied. The print stylesheet in mwpa.css pins each `.print-include`
     * card to A4-landscape printable width, hides AdminLTE chrome, and
     * reveals the `cross-species-print-desc` blocks. We re-render charts
     * once at the constrained width (D3 hard-codes `width="…"` on the SVG
     * from `host.clientWidth`) and again after print to restore normal
     * on-screen layout.
     */
    async _generatePdf() {
        const $body = jQuery('body');
        $body.addClass('mwpa-print-mode mwpa-print-mode-species');
        // Layout settle: requestAnimationFrame guarantees the new CSS width
        // has been applied before D3 re-measures host.clientWidth.
        await new Promise((resolve) => {
            window.requestAnimationFrame(() => resolve());
        });
        this._renderAll();
        const cleanup = () => {
            $body.removeClass('mwpa-print-mode mwpa-print-mode-species');
            window.removeEventListener('afterprint', cleanup);
            // Re-render so the on-screen layout (now back to wide) gets
            // SVGs sized for the dashboard container, not the print page.
            this._renderAll();
        };
        window.addEventListener('afterprint', cleanup);
        try {
            window.print();
        }
        finally {
            // Belt-and-braces: some browsers don't fire afterprint reliably.
            window.setTimeout(cleanup, 1500);
        }
    }
    _escape(s) {
        return s.replace(/[&<>"']/gu, (ch) => { var _a; return (_a = ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', '\'': '&#39;' })[ch]) !== null && _a !== void 0 ? _a : ch; });
    }
    /**
     * Pick stable per-species colors from the categorical palette. The
     * species are sorted by total point count (descending, across all
     * charts) so the most prominent species reliably get the most
     * distinctive palette positions (early entries are also the most
     * distinct hues by design).
     */
    static _buildColorMap(charts) {
        var _a;
        const pointsBySpecies = new Map();
        for (const chart of charts) {
            for (const series of chart.series) {
                pointsBySpecies.set(series.species_id, ((_a = pointsBySpecies.get(series.species_id)) !== null && _a !== void 0 ? _a : 0) + series.points.length);
            }
        }
        const ranked = [...pointsBySpecies.entries()]
            .sort((a, b) => b[1] - a[1])
            .map(([id]) => id);
        const out = new Map();
        for (let i = 0; i < ranked.length; i++) {
            out.set(ranked[i], CrossSpeciesAnalytics.SPECIES_PALETTE[i % CrossSpeciesAnalytics.SPECIES_PALETTE.length]);
        }
        return out;
    }
    /**
     * Render one scatter chart: points colored by species + per-species
     * regression line (when fit is present) + pooled regression line.
     * The pooled line is drawn last and thicker so it visually dominates;
     * when its slope sign disagrees with most colored lines, that's the
     * Simpson paradox moment.
     */
    static _renderScatter(host, chart, colorBySpecies) {
        var _a, _b;
        d3.select(host).selectAll('*').remove();
        jQuery(host).empty();
        const lang = Lang.i();
        const points = [];
        for (const s of chart.series) {
            for (const p of s.points) {
                points.push(p);
            }
        }
        if (points.length === 0) {
            jQuery(host).text(lang.l('No data'));
            return;
        }
        const width = Math.max(360, (host.clientWidth || 900) - 24);
        const height = 360;
        const margin = { top: 12, right: 16, bottom: 48, left: 56 };
        const innerW = width - margin.left - margin.right;
        const innerH = height - margin.top - margin.bottom;
        const svg = d3.select(host).append('svg').attr('width', width).attr('height', height);
        const g = svg.append('g').attr('transform', `translate(${margin.left},${margin.top})`);
        const xExtent = d3.extent(points, (p) => p.x);
        const yExtent = d3.extent(points, (p) => p.y);
        const xPad = (xExtent[1] - xExtent[0]) * 0.05 || 1;
        const yPad = (yExtent[1] - yExtent[0]) * 0.05 || 1;
        const x = d3.scaleLinear().domain([xExtent[0] - xPad, xExtent[1] + xPad]).nice().range([0, innerW]);
        const y = d3.scaleLinear().domain([Math.min(0, yExtent[0]) - yPad, yExtent[1] + yPad]).nice().range([innerH, 0]);
        // Axes + labels
        g.append('g').attr('transform', `translate(0,${innerH})`).call(d3.axisBottom(x).ticks(8));
        g.append('g').call(d3.axisLeft(y).ticks(6));
        g.append('text')
            .attr('x', innerW / 2)
            .attr('y', innerH + 36)
            .attr('text-anchor', 'middle')
            .style('font-size', '11px')
            .style('fill', '#6c757d')
            .text(lang.l(chart.x_label_key));
        g.append('text')
            .attr('transform', 'rotate(-90)')
            .attr('x', -innerH / 2)
            .attr('y', -margin.left + 16)
            .attr('text-anchor', 'middle')
            .style('font-size', '11px')
            .style('fill', '#6c757d')
            .text(lang.l(chart.y_label_key));
        // Points — per-series so we can color them and tooltip them.
        for (const series of chart.series) {
            const color = (_a = colorBySpecies.get(series.species_id)) !== null && _a !== void 0 ? _a : series.color;
            g.append('g')
                .selectAll('circle')
                .data(series.points)
                .enter()
                .append('circle')
                .attr('cx', (p) => x(p.x))
                .attr('cy', (p) => y(p.y))
                .attr('r', 3)
                .attr('fill', color)
                .attr('opacity', 0.65)
                .append('title')
                .text((p) => { var _a; return (_a = p.label) !== null && _a !== void 0 ? _a : `${p.x.toFixed(2)}, ${p.y.toFixed(2)}`; });
        }
        // Per-species regression lines (only when fit present).
        const xDomain = x.domain();
        for (const series of chart.series) {
            if (!series.fit) {
                continue;
            }
            const color = (_b = colorBySpecies.get(series.species_id)) !== null && _b !== void 0 ? _b : series.color;
            CrossSpeciesAnalytics._drawFitLine(g, x, y, series.fit, xDomain, color, false);
        }
        // Pooled fit (thicker, dashed black). Drawn last so it sits on top.
        if (chart.pooled_fit) {
            CrossSpeciesAnalytics._drawFitLine(g, x, y, chart.pooled_fit, xDomain, '#000', true);
        }
        CrossSpeciesAnalytics._buildLegend(host, chart, colorBySpecies);
    }
    /**
     * Draw one regression line clipped to the x scale's domain.
     * `dashed=true` is used for the pooled line; per-species lines use
     * the same color as their points.
     */
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    static _drawFitLine(g, x, y, fit, xDomain, color, dashed) {
        const x0 = xDomain[0];
        const x1 = xDomain[1];
        const y0 = (fit.slope * x0) + fit.intercept;
        const y1 = (fit.slope * x1) + fit.intercept;
        g.append('line')
            .attr('x1', x(x0))
            .attr('y1', y(y0))
            .attr('x2', x(x1))
            .attr('y2', y(y1))
            .attr('stroke', color)
            .attr('stroke-width', dashed ? 2.5 : 1.5)
            .attr('opacity', dashed ? 1 : 0.85)
            .attr('stroke-dasharray', dashed ? '6,4' : null);
    }
    /**
     * Color-chip legend with slope + R² for every series that has a fit,
     * plus a dedicated entry for the pooled fit. Helps the reader spot a
     * sign mismatch (Simpson) at a glance.
     */
    static _buildLegend(host, chart, colorBySpecies) {
        var _a;
        const lang = Lang.i();
        const legend = jQuery('<div class="mt-2 small d-flex flex-wrap" style="gap: 0.4rem 1rem;"/>');
        const escape = (s) => s.replace(/[&<>"']/g, (ch) => { var _a; return (_a = ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', '\'': '&#39;' })[ch]) !== null && _a !== void 0 ? _a : ch; });
        const slopeLabel = (fit) => `${lang.l('slope')} ${fit.slope.toFixed(3)} · R² ${fit.r2.toFixed(2)} · n=${fit.n}`;
        const renderRow = (color, label, fit, n, dashed) => {
            const dashBg = dashed
                ? 'background: repeating-linear-gradient(90deg, #000 0 4px, transparent 4px 8px);'
                : `background:${color};`;
            const slopeBit = fit ? `<span class="text-muted">${slopeLabel(fit)}</span>` : `<span class="text-muted">n=${n} · ${lang.l(`< min_n (no fit)`)}</span>`;
            return jQuery('<span class="d-inline-flex align-items-center" style="gap:0.35rem;">' +
                `<span style="display:inline-block;width:14px;height:3px;${dashBg}"></span>` +
                `<span>${escape(label)}</span>${slopeBit}</span>`);
        };
        for (const s of chart.series) {
            const color = (_a = colorBySpecies.get(s.species_id)) !== null && _a !== void 0 ? _a : s.color;
            legend.append(renderRow(color, s.species_name, s.fit, s.points.length, false));
        }
        if (chart.pooled_fit) {
            legend.append(renderRow('#000', `${lang.l('Pooled fit')} ${lang.l('(all species)')}`, chart.pooled_fit, chart.pooled_fit.n, true));
        }
        jQuery(host).append(legend);
    }
    /**
     * Same pattern as Species/Profile._attachInfo — Bootstrap-4 popover
     * behind a small (i) icon next to the card title.
     */
    static _attachInfo(card, titleKey, descKey) {
        const lang = Lang.i();
        const tools = card.getToolsElement();
        const escape = (s) => s.replace(/[&<>"']/g, (ch) => { var _a; return (_a = ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', '\'': '&#39;' })[ch]) !== null && _a !== void 0 ? _a : ch; });
        const btn = jQuery('<button type="button" class="btn btn-link btn-sm cross-species-info" ' +
            'style="padding:0 4px;color:#2c7fb8;text-decoration:none;line-height:1;" ' +
            `title="${escape(lang.l('Show description'))}">` +
            '<i class="fa fa-info-circle"></i></button>');
        tools.append(btn);
        UtilBootstrap.attachInfoPopover(btn, {
            html: true,
            sanitize: false,
            trigger: 'click',
            placement: 'left',
            container: 'body',
            title: escape(lang.l(titleKey)),
            content: `<div style="font-size:0.85rem;line-height:1.45;max-width:380px;">${escape(lang.l(descKey))}</div>`
        }, '.cross-species-info', 'cross-species-info');
    }
}
/**
 * Page name used by the sidebar.
 */
CrossSpeciesAnalytics.NAME = 'cross-species-analytics';
/**
 * Categorical palette (Tableau-10-style). The species_group color
 * coming from the DB is one of only ~3 blues, so 8 species rendered
 * with that scheme collapse visually — we override per species_id
 * with this distinct-hue palette, stable across all four charts.
 */
CrossSpeciesAnalytics.SPECIES_PALETTE = [
    '#1f77b4', '#ff7f0e', '#2ca02c', '#d62728', '#9467bd',
    '#8c564b', '#e377c2', '#7f7f7f', '#bcbd22', '#17becf'
];
